import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/site/Sections";
import { Field, TextArea } from "@/components/site/FormControls";
import { submitContact } from "@/lib/api";
import { BRAND } from "@/data/content";
import { Phone, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Message sent — we'll respond shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHero eyebrow="Contact" title="Talk to a Coordinator." subtitle="Confirm coverage, ask about a property, or start a financing review." />

      <section className="py-16 lg:py-24">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-[1fr_1.4fr] lg:px-8">
          <div className="space-y-4">
            <ContactCard icon={Phone} label="Phone" value={BRAND.phone} href={BRAND.phoneHref} />
            <ContactCard icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} />
            {BRAND.offices.map((o) => <ContactCard key={o.name} icon={MapPin} label={o.name} value={o.address} href={o.href} />)}
            <ContactCard icon={MapPin} label="Languages" value="English · Português" />
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <p className="text-sm leading-relaxed text-slate-500">{BRAND.taglineSecondary}</p>
              <p className="mt-3 font-mono-x text-xs tracking-widest text-gold">{BRAND.motto}</p>
            </div>
          </div>

          {done ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center">
              <CheckCircle2 className="h-16 w-16 text-gold" />
              <p className="mt-6 text-base text-slate-600">Thanks for reaching out. A Vertexhub coordinator will respond shortly.</p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Name" name="name" value={form.name} onChange={set} required half testid="contact-name" />
                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set} half testid="contact-phone" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={set} required testid="contact-email" />
                <Field label="Subject" name="subject" value={form.subject} onChange={set} testid="contact-subject" />
                <TextArea label="Message" name="message" value={form.message} onChange={set} required placeholder="How can we help?" testid="contact-message" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-form-submit-btn"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-semibold text-[#0B132B] transition-all hover:bg-[#E5C158] disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const ContactCard = ({ icon: Icon, label, value, href }) => {
  const inner = (
    <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-gold/40">
      <div className="rounded-lg border border-gold/30 bg-gold/5 p-3"><Icon className="h-5 w-5 text-gold" /></div>
      <div>
        <div className="font-mono-x text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-sm font-semibold text-[#0B132B]">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{inner}</a> : inner;
};
